import React, { useState, useEffect, useCallback } from 'react';
import {
  LifeBuoy, ArrowLeft, AlertTriangle, CheckCircle2, XCircle,
  Rocket, Wrench, Package, Truck, RotateCcw, User as UserIcon,
  Bug, Lightbulb, Sparkles, HelpCircle, X, Bot, Send, RefreshCw,
  MessageSquare, Edit2, Paperclip, FileText, Image as ImageIcon,
  Download, Trash2, ExternalLink,
} from 'lucide-react';
import { colors, API, authHeaders } from '../utils/theme';
import useIsMobile from '../hooks/useIsMobile';

// ─────────────────────────────────────
// Helpers de display
// ─────────────────────────────────────
const STATUS_LABEL = {
  ABERTO: 'Aberto',
  APROVADO_GESTOR: 'Aprovado (gestor)',
  LIBERADO_DEV: 'Liberado p/ Dev',
  EM_DESENVOLVIMENTO: 'Em desenvolvimento',
  AGUARDANDO_FEEDBACK: 'Aguardando feedback',
  ENTREGUE: 'Entregue',
  REJEITADO_GESTOR: 'Rejeitado (gestor)',
  REJEITADO_ADMIN: 'Rejeitado (admin)',
  REJEITADO_USUARIO: 'Rejeitado (usuário)',
};

const STATUS_COLOR = {
  ABERTO: colors.textMuted,
  APROVADO_GESTOR: colors.info,
  LIBERADO_DEV: '#8b5cf6',
  EM_DESENVOLVIMENTO: colors.warning,
  AGUARDANDO_FEEDBACK: '#f97316',
  ENTREGUE: colors.success,
  REJEITADO_GESTOR: colors.danger,
  REJEITADO_ADMIN: colors.danger,
  REJEITADO_USUARIO: colors.danger,
};

const CATEGORIA_LABEL = {
  bug: 'Bug', melhoria: 'Melhoria', nova_feature: 'Nova Feature', duvida: 'Dúvida',
};

const CATEGORIA_COLOR = {
  bug: colors.danger, melhoria: colors.info, nova_feature: '#8b5cf6', duvida: colors.warning,
};

const PRIORIDADE_LABEL = {
  baixa: 'Baixa', media: 'Média', alta: 'Alta', critica: 'Crítica',
};

const PRIORIDADE_COLOR = {
  baixa: colors.textDim, media: colors.info, alta: colors.warning, critica: colors.danger,
};

function fmtDateTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function CategoriaIcon({ categoria, size = 14 }) {
  const props = { size };
  if (categoria === 'bug') return <Bug {...props} />;
  if (categoria === 'melhoria') return <Lightbulb {...props} />;
  if (categoria === 'nova_feature') return <Sparkles {...props} />;
  return <HelpCircle {...props} />;
}

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || colors.textMuted;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: `${color}22`, color, border: `1px solid ${color}44`,
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ─────────────────────────────────────
// ChamadoDetalhePage
// ─────────────────────────────────────
export default function ChamadoDetalhePage({ onNavigate }) {
  const chamadoId = localStorage.getItem('chamado_id_atual');
  const [chamado, setChamado] = useState(null);
  const [comentarios, setComentarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [novoComentario, setNovoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [modalRejeicao, setModalRejeicao] = useState(null); // 'gestor' | 'admin' | 'usuario'
  const [modalFeedback, setModalFeedback] = useState(false);
  const [modalComentario, setModalComentario] = useState(null); // { acao, titulo, extraBody }
  const [modalEditar, setModalEditar] = useState(false);
  const [modalReabrirMelhoria, setModalReabrirMelhoria] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState(null);
  const mobile = useIsMobile();
  const [analisandoIA, setAnalisandoIA] = useState(false);
  const [anexos, setAnexos] = useState([]);

  const usuario = JSON.parse(localStorage.getItem('xm_usuario') || '{}');
  const isAdmin = (usuario.perfis || []).includes('ADMIN');

  const fetchChamado = useCallback(async () => {
    if (!chamadoId) return;
    setLoading(true);
    setError(null);
    try {
      const [resChamado, resComentarios, resAnexos] = await Promise.all([
        fetch(`${API}/chamados/${chamadoId}`, { headers: authHeaders() }),
        fetch(`${API}/chamados/${chamadoId}/comentarios`, { headers: authHeaders() }),
        fetch(`${API}/chamados/${chamadoId}/anexos`, { headers: authHeaders() }),
      ]);
      if (!resChamado.ok) {
        const d = await resChamado.json();
        throw new Error(d.error || `HTTP ${resChamado.status}`);
      }
      const [chamadoData, comentData, anexosData] = await Promise.all([
        resChamado.json(),
        resComentarios.json(),
        resAnexos.ok ? resAnexos.json() : { data: [] },
      ]);
      setChamado(chamadoData);
      setComentarios(comentData.data || []);
      setAnexos(anexosData.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [chamadoId]);

  useEffect(() => { fetchChamado(); }, [fetchChamado]);

  const callAction = async (acao, body = {}) => {
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/${acao}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchChamado();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActionPending(false);
    }
  };

  const enviarComentario = async () => {
    if (!novoComentario.trim()) return;
    setEnviandoComentario(true);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/comentarios`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ texto: novoComentario.trim() }),
      });
      if (!res.ok) throw new Error('Erro ao enviar comentário');
      setNovoComentario('');
      await fetchChamado();
    } finally {
      setEnviandoComentario(false);
    }
  };

  const salvarEdicao = async (campos) => {
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(campos),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchChamado();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActionPending(false);
    }
  };

  const reabrirMelhoria = async (infoAdicional) => {
    setActionPending(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/reabrir-melhoria`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ info_adicional: infoAdicional }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchChamado();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActionPending(false);
    }
  };

  const excluirAnexo = async (anexoId) => {
    if (!window.confirm('Excluir este anexo?')) return;
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/anexos/${anexoId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Erro ao excluir');
      }
      await fetchChamado();
    } catch (e) {
      setActionError(e.message);
    }
  };

  const analisarIA = async () => {
    setAnalisandoIA(true);
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/analisar-ia`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Erro ao analisar com IA');
      }
      await fetchChamado();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setAnalisandoIA(false);
    }
  };

  if (!chamadoId) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
        <LifeBuoy size={36} style={{ opacity: 0.3, marginBottom: 12 }} />
        <div>Nenhum chamado selecionado.</div>
        <button onClick={() => onNavigate('chamados')} style={{ marginTop: 12, background: 'none', border: `1px solid ${colors.border}`, color: colors.textMuted, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
          Voltar aos chamados
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
        <LifeBuoy size={28} style={{ opacity: 0.5, marginBottom: 8 }} />
        <div>Carregando chamado...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button onClick={() => onNavigate('chamados')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: `1px solid ${colors.border}`, color: colors.textMuted, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
          <ArrowLeft size={14} /> Voltar
        </button>
        <div style={{ padding: 20, background: `${colors.danger}15`, border: `1px solid ${colors.danger}40`, borderRadius: 10, color: colors.danger, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div><strong>Erro ao carregar chamado</strong><br /><span style={{ fontSize: 12 }}>{error}</span></div>
        </div>
      </div>
    );
  }

  if (!chamado) return null;

  const isCriador = chamado.criador_id === usuario.id;
  const catColor = CATEGORIA_COLOR[chamado.categoria] || colors.textMuted;
  const priColor = PRIORIDADE_COLOR[chamado.prioridade] || colors.textMuted;
  const podeEditar = isAdmin || (isCriador && chamado.status === 'ABERTO');
  const podeReabrirMelhoria = (isCriador || isAdmin) && (chamado.status === 'ENTREGUE' || chamado.status === 'REJEITADO_USUARIO');

  return (
    <div>
      {/* Voltar */}
      <button
        onClick={() => onNavigate('chamados')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
          background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 13, padding: 0,
        }}
      >
        <ArrowLeft size={13} /> Voltar aos chamados
      </button>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: `${catColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: catColor,
          }}>
            <CategoriaIcon categoria={chamado.categoria} size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.text }}>
              #{chamado.id} — {chamado.titulo}
            </h1>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
              {CATEGORIA_LABEL[chamado.categoria]} · por {chamado.criador_nome} · {fmtDateTime(chamado.created_at)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {podeEditar && (
              <button
                onClick={() => setModalEditar(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', background: `${colors.primary}22`,
                  border: `1px solid ${colors.primary}44`, borderRadius: 8,
                  color: colors.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Edit2 size={12} /> Editar
              </button>
            )}
            {podeReabrirMelhoria && (
              <button
                onClick={() => setModalReabrirMelhoria(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', background: `${colors.warning}22`,
                  border: `1px solid ${colors.warning}44`, borderRadius: 8,
                  color: colors.warning, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <RotateCcw size={12} /> Reabrir para melhoria
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge status={chamado.status} />
          <span style={{
            padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700,
            background: `${priColor}22`, color: priColor,
          }}>
            Prioridade: {PRIORIDADE_LABEL[chamado.prioridade]}
          </span>
        </div>
      </div>

      {/* Layout 2 colunas (no celular, a lateral vai pra baixo) */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : '1fr 320px', gap: mobile ? 14 : 20, alignItems: 'start' }}>
        {/* Coluna principal */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Descrição */}
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Descrição</div>
            <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {chamado.descricao}
            </div>
          </div>

          {/* Rejeições */}
          {chamado.status === 'REJEITADO_GESTOR' && chamado.motivo_rejeicao_gestor && (
            <div style={{ background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.danger, marginBottom: 8 }}>
                Motivo da rejeição — {chamado.rejeitado_gestor_nome || 'gestor'}
              </div>
              <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap' }}>{chamado.motivo_rejeicao_gestor}</div>
            </div>
          )}
          {chamado.status === 'REJEITADO_ADMIN' && chamado.motivo_rejeicao_admin && (
            <div style={{ background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.danger, marginBottom: 8 }}>
                Motivo da rejeição — {chamado.rejeitado_admin_nome || 'admin'}
              </div>
              <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap' }}>{chamado.motivo_rejeicao_admin}</div>
            </div>
          )}

          {/* Feedback do criador */}
          {chamado.feedback_usuario && (
            <div style={{
              background: chamado.aprovado_usuario_em ? `${colors.success}10` : `${colors.danger}10`,
              border: `1px solid ${chamado.aprovado_usuario_em ? colors.success : colors.danger}30`,
              borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: chamado.aprovado_usuario_em ? colors.success : colors.danger, marginBottom: 8 }}>
                {chamado.aprovado_usuario_em ? 'Feedback positivo do criador' : 'Feedback negativo do criador'}
              </div>
              <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap' }}>{chamado.feedback_usuario}</div>
            </div>
          )}

          {/* Notas de desenvolvimento */}
          {chamado.notas_desenvolvimento && (
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notas de desenvolvimento</div>
              <div style={{ fontSize: 12, color: colors.text, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{chamado.notas_desenvolvimento}</div>
            </div>
          )}

          {/* Anexos */}
          <AnexosSection
            anexos={anexos}
            chamadoId={chamadoId}
            isAdmin={isAdmin}
            isCriador={isCriador}
            onExcluir={excluirAnexo}
          />

          {/* Análise IA */}
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bot size={16} color={colors.info} />
                <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Análise da IA</span>
              </div>
              <button
                onClick={analisarIA}
                disabled={analisandoIA}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', background: analisandoIA ? colors.textDim : `${colors.info}22`,
                  border: `1px solid ${colors.info}40`, borderRadius: 6,
                  color: colors.info, fontSize: 12, cursor: analisandoIA ? 'not-allowed' : 'pointer',
                }}
              >
                <RefreshCw size={12} style={analisandoIA ? { animation: 'spin 1s linear infinite' } : {}} />
                {analisandoIA ? 'Analisando...' : chamado.ia_analise ? 'Reanalisar' : 'Analisar com IA'}
              </button>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>

            {!chamado.ia_analise && !analisandoIA && (
              <div style={{ fontSize: 13, color: colors.textDim, fontStyle: 'italic' }}>
                Análise não gerada ainda. Clique em "Analisar com IA" para obter diagnóstico e sugestão de implementação.
              </div>
            )}
            {analisandoIA && (
              <div style={{ fontSize: 13, color: colors.info }}>
                Consultando Claude... pode levar alguns segundos.
              </div>
            )}
            {chamado.ia_analise && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>DIAGNÓSTICO</div>
                <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, marginBottom: 14 }}>
                  {chamado.ia_analise}
                </div>
                {chamado.ia_sugestao && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>SUGESTÃO DE IMPLEMENTAÇÃO</div>
                    <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {chamado.ia_sugestao}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Ações */}
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Ações disponíveis</div>
            <AcoesChamado
              chamado={chamado}
              isCriador={isCriador}
              isAdmin={isAdmin}
              isPending={actionPending}
              onAcao={callAction}
              onAcaoComComentario={(acao, titulo, extraFields) => setModalComentario({ acao, titulo, extraFields: extraFields || [] })}
              onRejeitarGestor={() => setModalRejeicao('gestor')}
              onRejeitarAdmin={() => setModalRejeicao('admin')}
              onRejeitarUsuario={() => setModalRejeicao('usuario')}
              onAprovarFeedback={() => setModalFeedback(true)}
            />
            {actionError && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: `${colors.danger}15`, border: `1px solid ${colors.danger}40`, borderRadius: 6, fontSize: 12, color: colors.danger }}>
                {actionError}
              </div>
            )}
          </div>

          {/* Comentários */}
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Comentários ({comentarios.filter(c => !c.is_ia).length})
            </div>

            {comentarios.filter(c => !c.is_ia).length === 0 && (
              <div style={{ fontSize: 13, color: colors.textDim, fontStyle: 'italic', marginBottom: 14 }}>
                Nenhum comentário ainda.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {comentarios.filter(c => !c.is_ia).map(c => (
                <div key={c.id} style={{
                  padding: '10px 12px', background: colors.bg,
                  border: `1px solid ${colors.border}`, borderRadius: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      background: `${colors.primary}30`, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 10, fontWeight: 700, color: colors.primary,
                    }}>
                      {c.autor_nome[0].toUpperCase()}
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{c.autor_nome}</span>
                    <span style={{ fontSize: 11, color: colors.textDim, marginLeft: 'auto' }}>{fmtDateTime(c.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: colors.text, whiteSpace: 'pre-wrap', paddingLeft: 32 }}>
                    {c.texto}
                  </div>
                </div>
              ))}
            </div>

            {/* Novo comentário */}
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea
                value={novoComentario}
                onChange={e => setNovoComentario(e.target.value)}
                rows={2}
                placeholder="Adicione um comentário..."
                style={{
                  flex: 1, padding: '8px 12px', background: colors.bg,
                  border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
                  fontSize: 13, resize: 'vertical', fontFamily: 'inherit',
                }}
                onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) enviarComentario(); }}
              />
              <button
                onClick={enviarComentario}
                disabled={!novoComentario.trim() || enviandoComentario}
                style={{
                  padding: '8px 14px', background: novoComentario.trim() ? colors.primary : colors.textDim,
                  border: 'none', borderRadius: 8, color: colors.white, cursor: novoComentario.trim() ? 'pointer' : 'not-allowed',
                  alignSelf: 'flex-end',
                }}
              >
                <Send size={14} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4 }}>Ctrl+Enter para enviar</div>
          </div>
        </div>

        {/* Coluna lateral — Timeline */}
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Histórico
          </div>
          <TimelineChamado chamado={chamado} />
        </div>
      </div>

      {/* Modais */}
      {modalRejeicao && (
        <ModalRejeicao
          tipo={modalRejeicao}
          onClose={() => setModalRejeicao(null)}
          onConfirmar={(motivo) => {
            const acao = modalRejeicao === 'gestor' ? 'rejeitar-gestor'
              : modalRejeicao === 'admin' ? 'rejeitar-admin'
              : 'rejeitar-feedback';
            const body = modalRejeicao === 'usuario' ? { feedback: motivo } : { motivo };
            callAction(acao, body);
            setModalRejeicao(null);
          }}
        />
      )}

      {modalFeedback && (
        <ModalAprovarFeedback
          onClose={() => setModalFeedback(false)}
          onConfirmar={(feedback, comentario) => {
            const body = {};
            if (feedback) body.feedback = feedback;
            if (comentario) body.comentario = comentario;
            callAction('aprovar-feedback', body);
            setModalFeedback(false);
          }}
        />
      )}

      {modalComentario && (
        <ModalComentarioAcao
          titulo={modalComentario.titulo}
          extraFields={modalComentario.extraFields}
          onClose={() => setModalComentario(null)}
          onConfirmar={(comentario, extras) => {
            const body = { ...extras };
            if (comentario) body.comentario = comentario;
            callAction(modalComentario.acao, body);
            setModalComentario(null);
          }}
        />
      )}

      {modalEditar && (
        <ModalEditarChamado
          chamado={chamado}
          onClose={() => setModalEditar(false)}
          onSalvar={(campos) => {
            salvarEdicao(campos);
            setModalEditar(false);
          }}
        />
      )}

      {modalReabrirMelhoria && (
        <ModalReabrirMelhoria
          onClose={() => setModalReabrirMelhoria(false)}
          onConfirmar={(infoAdicional) => {
            reabrirMelhoria(infoAdicional);
            setModalReabrirMelhoria(false);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────
// Botões de ação
// ─────────────────────────────────────
function AcoesChamado({
  chamado, isCriador, isAdmin, isPending,
  onAcao, onAcaoComComentario, onRejeitarGestor, onRejeitarAdmin, onRejeitarUsuario, onAprovarFeedback
}) {
  const { status } = chamado;
  const btnBase = { padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: isPending ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: isPending ? 0.7 : 1 };
  const btnPrimary = { ...btnBase, background: colors.primary, color: colors.white };
  const btnDanger = { ...btnBase, background: `${colors.danger}22`, color: colors.danger, border: `1px solid ${colors.danger}40` };
  const btnSecondary = { ...btnBase, background: colors.bg, color: colors.textMuted, border: `1px solid ${colors.border}` };

  if (status === 'ABERTO' && isAdmin) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => onAcaoComComentario('aprovar-gestor', 'Aprovar ideia')} disabled={isPending} style={btnPrimary}>
          <CheckCircle2 size={14} /> Aprovar ideia
        </button>
        <button onClick={onRejeitarGestor} disabled={isPending} style={btnDanger}>
          <XCircle size={14} /> Rejeitar (com motivo)
        </button>
      </div>
    );
  }

  if (status === 'APROVADO_GESTOR' && isAdmin) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => onAcaoComComentario('liberar-dev', 'Liberar pra codar')} disabled={isPending} style={btnPrimary}>
          <Rocket size={14} /> Liberar pra codar
        </button>
        <button onClick={onRejeitarAdmin} disabled={isPending} style={btnDanger}>
          <XCircle size={14} /> Rejeitar (com motivo)
        </button>
      </div>
    );
  }

  if (status === 'LIBERADO_DEV' && isAdmin) {
    return (
      <button
        onClick={() => onAcaoComComentario('iniciar-dev', 'Marcar em desenvolvimento', [{ key: 'notas', label: 'Notas de desenvolvimento (opcional)', placeholder: 'Notas técnicas, branch, etc.' }])}
        disabled={isPending}
        style={btnPrimary}
      >
        <Wrench size={14} /> Marcar em desenvolvimento
      </button>
    );
  }

  if (status === 'EM_DESENVOLVIMENTO' && isAdmin) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => onAcaoComComentario('marcar-pronto', 'Marcar como pronto', [{ key: 'notas', label: 'Notas de entrega (opcional)', placeholder: 'Resumo do que foi feito, como testar, etc.' }])}
          disabled={isPending}
          style={btnPrimary}
        >
          <Package size={14} /> Marcar como pronto (envia pra homologação)
        </button>
        <button onClick={() => onAcao('reabrir')} disabled={isPending} style={btnSecondary}>
          <RotateCcw size={14} /> Reabrir (voltar pra fila)
        </button>
      </div>
    );
  }

  if (status === 'AGUARDANDO_FEEDBACK' && isCriador) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, color: colors.textMuted, padding: '8px 12px', background: `${colors.warning}10`, border: `1px solid ${colors.warning}30`, borderRadius: 6 }}>
          Este chamado foi marcado como pronto pelo time de dev. Teste a implementação e nos diga se atendeu sua solicitação.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={onAprovarFeedback} disabled={isPending} style={btnPrimary}>
            <CheckCircle2 size={14} /> Aprovar e fechar (vai pra Entregue)
          </button>
          <button onClick={onRejeitarUsuario} disabled={isPending} style={btnDanger}>
            <XCircle size={14} /> Não resolveu — explicar problema
          </button>
        </div>
      </div>
    );
  }

  if (status === 'AGUARDANDO_FEEDBACK' && isAdmin && !isCriador) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, color: colors.textMuted }}>
          Aguardando o criador (<strong>{chamado.criador_nome}</strong>) aprovar.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onAcao('reabrir')} disabled={isPending} style={btnSecondary}>
            <RotateCcw size={14} /> Reabrir (voltar pra dev)
          </button>
        </div>
      </div>
    );
  }

  if (status === 'REJEITADO_USUARIO' && isAdmin) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, color: colors.textMuted }}>
          O criador rejeitou a entrega. Você pode reabrir para a fila de dev.
        </div>
        <button onClick={() => onAcao('reabrir')} disabled={isPending} style={btnPrimary}>
          <RotateCcw size={14} /> Reabrir e devolver pra fila Dev
        </button>
      </div>
    );
  }

  if ((status === 'ENTREGUE' || status === 'REJEITADO_GESTOR' || status === 'REJEITADO_ADMIN') && !isAdmin) {
    return (
      <div style={{ fontSize: 13, color: colors.textDim, fontStyle: 'italic' }}>
        Nenhuma ação disponível no status atual para seu perfil.
      </div>
    );
  }

  return (
    <div style={{ fontSize: 13, color: colors.textDim, fontStyle: 'italic' }}>
      Nenhuma ação disponível no status atual para seu perfil.
    </div>
  );
}

// ─────────────────────────────────────
// Timeline
// ─────────────────────────────────────
function TimelineChamado({ chamado }) {
  const eventos = [];

  eventos.push({ label: 'Chamado aberto', quando: chamado.created_at, quem: chamado.criador_nome, Icon: UserIcon, color: colors.textMuted });
  if (chamado.aprovado_gestor_em) eventos.push({ label: 'Aprovado pelo gestor', quando: chamado.aprovado_gestor_em, quem: chamado.aprovado_gestor_nome, Icon: CheckCircle2, color: colors.info });
  if (chamado.rejeitado_gestor_em) eventos.push({ label: 'Rejeitado pelo gestor', quando: chamado.rejeitado_gestor_em, quem: chamado.rejeitado_gestor_nome, Icon: XCircle, color: colors.danger });
  if (chamado.liberado_admin_em) eventos.push({ label: 'Liberado pra dev', quando: chamado.liberado_admin_em, quem: chamado.liberado_admin_nome, Icon: Rocket, color: colors.success });
  if (chamado.rejeitado_admin_em) eventos.push({ label: 'Rejeitado pelo admin', quando: chamado.rejeitado_admin_em, quem: chamado.rejeitado_admin_nome, Icon: XCircle, color: colors.danger });
  if (chamado.em_desenvolvimento_em) eventos.push({ label: 'Em desenvolvimento', quando: chamado.em_desenvolvimento_em, Icon: Wrench, color: colors.warning });
  if (chamado.pronto_em) eventos.push({ label: 'Pronto (homologação)', quando: chamado.pronto_em, Icon: Package, color: colors.info });
  if (chamado.aguardando_feedback_em && !chamado.aprovado_usuario_em && !chamado.rejeitado_usuario_em) {
    eventos.push({ label: `Aguardando feedback (${chamado.criador_nome})`, quando: chamado.aguardando_feedback_em, Icon: MessageSquare, color: '#f97316' });
  }
  if (chamado.aprovado_usuario_em) eventos.push({ label: 'Criador aprovou a entrega', quando: chamado.aprovado_usuario_em, quem: chamado.criador_nome, Icon: CheckCircle2, color: colors.success });
  if (chamado.rejeitado_usuario_em) eventos.push({ label: 'Criador rejeitou', quando: chamado.rejeitado_usuario_em, quem: chamado.criador_nome, Icon: XCircle, color: colors.danger });
  if (chamado.entregue_em) eventos.push({ label: 'Entregue em produção', quando: chamado.entregue_em, Icon: Truck, color: colors.success });

  eventos.sort((a, b) => new Date(a.quando) - new Date(b.quando));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {eventos.map((ev, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            background: `${ev.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: ev.color,
          }}>
            <ev.Icon size={13} />
          </div>
          <div style={{ flex: 1, paddingTop: 2 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>{ev.label}</div>
            <div style={{ fontSize: 11, color: colors.textDim }}>
              {fmtDateTime(ev.quando)}
              {ev.quem && ` · ${ev.quem}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────
// Modal de rejeição
// ─────────────────────────────────────
function ModalRejeicao({ tipo, onClose, onConfirmar }) {
  const [motivo, setMotivo] = useState('');

  const titulo = tipo === 'gestor' ? 'Rejeitar chamado (gestor)'
    : tipo === 'admin' ? 'Rejeitar chamado (admin)'
    : 'Não resolveu — explicar problema';
  const placeholder = tipo === 'usuario'
    ? 'Explique o que não funcionou ou o que ainda falta. O time de dev verá essa mensagem.'
    : 'Explique o motivo da rejeição. O solicitante verá essa mensagem.';

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.card, borderRadius: 16, width: '100%', maxWidth: 480, border: `1px solid ${colors.border}` }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, color: colors.text }}>{titulo}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            {tipo === 'usuario' ? 'O que ainda falta?' : 'Motivo da rejeição'}
          </label>
          <textarea
            autoFocus
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            maxLength={2000}
            rows={5}
            placeholder={placeholder}
            style={{
              width: '100%', padding: '8px 12px', background: colors.bg,
              border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 10, color: colors.textDim, marginTop: 3 }}>{motivo.length}/2000 — mínimo 3 caracteres</div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button
            onClick={() => onConfirmar(motivo.trim())}
            disabled={motivo.trim().length < 3}
            style={{
              padding: '8px 16px', background: motivo.trim().length >= 3 ? colors.danger : colors.textDim,
              border: 'none', borderRadius: 8, color: colors.white, fontSize: 13, fontWeight: 600,
              cursor: motivo.trim().length >= 3 ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <XCircle size={14} /> {tipo === 'usuario' ? 'Enviar feedback' : 'Confirmar rejeição'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal aprovar feedback
// ─────────────────────────────────────
function ModalAprovarFeedback({ onClose, onConfirmar }) {
  const [feedback, setFeedback] = useState('');
  const [comentario, setComentario] = useState('');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.card, borderRadius: 16, width: '100%', maxWidth: 480, border: `1px solid ${colors.border}` }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={18} color={colors.success} /> Aprovar entrega
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>
            Confirma que a implementação resolveu o que você pediu? O chamado vai para <strong>Entregue</strong>.
          </div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            Feedback (opcional)
          </label>
          <textarea
            autoFocus
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Ex: ficou ótimo, obrigado! / funcionou perfeitamente"
            style={{
              width: '100%', padding: '8px 12px', background: colors.bg,
              border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8, marginTop: 12 }}>
            Comentário (opcional)
          </label>
          <textarea
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Comentário adicional no histórico"
            style={{
              width: '100%', padding: '8px 12px', background: colors.bg,
              border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button
            onClick={() => onConfirmar(feedback.trim(), comentario.trim())}
            style={{
              padding: '8px 16px', background: colors.success, border: 'none', borderRadius: 8,
              color: colors.white, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <CheckCircle2 size={14} /> Aprovar e fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal comentário opcional em ação de status
// ─────────────────────────────────────
function ModalComentarioAcao({ titulo, extraFields = [], onClose, onConfirmar }) {
  const [comentario, setComentario] = useState('');
  const [extras, setExtras] = useState(() => {
    const init = {};
    extraFields.forEach(f => { init[f.key] = ''; });
    return init;
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.card, borderRadius: 16, width: '100%', maxWidth: 480, border: `1px solid ${colors.border}` }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, color: colors.text }}>{titulo}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          {extraFields.map((f, idx) => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
                {f.label}
              </label>
              <textarea
                autoFocus={idx === 0}
                value={extras[f.key]}
                onChange={e => setExtras({ ...extras, [f.key]: e.target.value })}
                rows={3}
                maxLength={5000}
                placeholder={f.placeholder || ''}
                style={{
                  width: '100%', padding: '8px 12px', background: colors.bg,
                  border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
                  fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
            </div>
          ))}
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            Comentário (opcional)
          </label>
          <textarea
            autoFocus={extraFields.length === 0}
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Comentário adicional no histórico do chamado"
            style={{
              width: '100%', padding: '8px 12px', background: colors.bg,
              border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button
            onClick={() => {
              const extrasFiltrados = {};
              Object.entries(extras).forEach(([k, v]) => { if (v.trim()) extrasFiltrados[k] = v.trim(); });
              onConfirmar(comentario.trim(), extrasFiltrados);
            }}
            style={{
              padding: '8px 16px', background: colors.primary, border: 'none', borderRadius: 8,
              color: colors.white, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <CheckCircle2 size={14} /> Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal editar chamado
// ─────────────────────────────────────
function ModalEditarChamado({ chamado, onClose, onSalvar }) {
  const [titulo, setTitulo] = useState(chamado.titulo || '');
  const [descricao, setDescricao] = useState(chamado.descricao || '');
  const [categoria, setCategoria] = useState(chamado.categoria || 'bug');
  const [prioridade, setPrioridade] = useState(chamado.prioridade || 'media');
  const [comentario, setComentario] = useState('');

  const inputStyle = {
    width: '100%', padding: '8px 12px', background: colors.bg,
    border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
    fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const podeSalvar = titulo.trim().length >= 3 && descricao.trim().length >= 3;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.card, borderRadius: 16, width: '100%', maxWidth: 560, border: `1px solid ${colors.border}`, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit2 size={16} color={colors.primary} /> Editar chamado
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Título</label>
            <input value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={200} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={5} maxLength={5000} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Categoria</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} style={inputStyle}>
                <option value="bug">Bug</option>
                <option value="melhoria">Melhoria</option>
                <option value="nova_feature">Nova Feature</option>
                <option value="duvida">Dúvida</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Prioridade</label>
              <select value={prioridade} onChange={e => setPrioridade(e.target.value)} style={inputStyle}>
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítica</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Comentário / justificativa (opcional)</label>
            <textarea value={comentario} onChange={e => setComentario(e.target.value)} rows={2} maxLength={2000} placeholder="Motivo da edição" style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button
            onClick={() => {
              const campos = {
                titulo: titulo.trim(),
                descricao: descricao.trim(),
                categoria,
                prioridade,
              };
              if (comentario.trim()) campos.comentario = comentario.trim();
              onSalvar(campos);
            }}
            disabled={!podeSalvar}
            style={{
              padding: '8px 16px', background: podeSalvar ? colors.primary : colors.textDim,
              border: 'none', borderRadius: 8, color: colors.white, fontSize: 13, fontWeight: 600,
              cursor: podeSalvar ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <CheckCircle2 size={14} /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Modal reabrir para melhoria
// ─────────────────────────────────────
function ModalReabrirMelhoria({ onClose, onConfirmar }) {
  const [info, setInfo] = useState('');
  const valido = info.trim().length >= 3;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: colors.card, borderRadius: 16, width: '100%', maxWidth: 480, border: `1px solid ${colors.border}` }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            <RotateCcw size={16} color={colors.warning} /> Reabrir para melhoria
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>
            O chamado volta para <strong>Liberado p/ Dev</strong> e a informação abaixo será registrada como comentário para o time.
          </div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            Descreva o que precisa ser melhorado
          </label>
          <textarea
            autoFocus
            value={info}
            onChange={e => setInfo(e.target.value)}
            rows={5}
            maxLength={5000}
            placeholder="Ex: adicionar filtro X na tela Y, ajustar cálculo Z..."
            style={{
              width: '100%', padding: '8px 12px', background: colors.bg,
              border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text,
              fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
            }}
          />
          <div style={{ fontSize: 10, color: colors.textDim, marginTop: 3 }}>{info.length}/5000 — mínimo 3 caracteres</div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button
            onClick={() => onConfirmar(info.trim())}
            disabled={!valido}
            style={{
              padding: '8px 16px', background: valido ? colors.warning : colors.textDim,
              border: 'none', borderRadius: 8, color: colors.white, fontSize: 13, fontWeight: 600,
              cursor: valido ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <RotateCcw size={14} /> Reabrir para melhoria
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// Seção de Anexos
// ─────────────────────────────────────
function AnexosSection({ anexos, chamadoId, isAdmin, isCriador, onExcluir }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('xm_token') : null;

  const fmtSize = (b) => {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const isImagem = (mime) => mime && mime.startsWith('image/');

  // Baixa anexo via fetch autenticado e força download
  const baixar = async (anexo) => {
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/anexos/${anexo.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Falha ao baixar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = anexo.nome_original;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erro ao baixar: ' + e.message);
    }
  };

  // Abre em nova aba via blob URL (com auth)
  const abrir = async (anexo) => {
    try {
      const res = await fetch(`${API}/chamados/${chamadoId}/anexos/${anexo.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Falha ao abrir');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      alert('Erro ao abrir: ' + e.message);
    }
  };

  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16 }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: colors.textMuted,
        marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Paperclip size={12} /> Anexos ({anexos.length})
      </div>
      {anexos.length === 0 && (
        <div style={{ fontSize: 13, color: colors.textDim, fontStyle: 'italic' }}>
          Nenhum anexo neste chamado.
        </div>
      )}
      {anexos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {anexos.map(a => {
            const podeExcluir = isAdmin || isCriador;
            return (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: 10, background: colors.bg,
                border: `1px solid ${colors.border}`, borderRadius: 8,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                  background: `${colors.primary}22`, color: colors.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isImagem(a.mime_type) ? <ImageIcon size={16} /> : <FileText size={16} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: colors.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.nome_original}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textDim }}>
                    {fmtSize(a.tamanho)} · {a.uploaded_nome}
                  </div>
                </div>
                <button
                  onClick={() => abrir(a)}
                  title="Abrir"
                  style={{
                    padding: '6px 10px', background: `${colors.info}22`,
                    border: `1px solid ${colors.info}44`, borderRadius: 6,
                    color: colors.info, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <ExternalLink size={11} /> Abrir
                </button>
                <button
                  onClick={() => baixar(a)}
                  title="Baixar"
                  style={{
                    padding: '6px 10px', background: 'transparent',
                    border: `1px solid ${colors.border}`, borderRadius: 6,
                    color: colors.text, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Download size={11} /> Baixar
                </button>
                {podeExcluir && (
                  <button
                    onClick={() => onExcluir(a.id)}
                    title="Excluir"
                    style={{
                      padding: '6px 8px', background: `${colors.danger}15`,
                      border: `1px solid ${colors.danger}40`, borderRadius: 6,
                      color: colors.danger, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center',
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
